import React, { useState, useEffect } from 'react'
import { api } from '@/api'
import Toast from '@/components/Toast'
import homeImg from '@/assets/images/home.jpg'
import detailsImg from '@/assets/images/details.jpg'
import walletImg from '@/assets/images/wallet.jpg'
import './Home.css'

const Home: React.FC = () => {
  const [isWeChat, setIsWeChat] = useState(false)
  const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' | 'info' | 'warning' }>({
    show: false,
    message: '',
    type: 'info'
  })

  useEffect(() => {
    // 检测是否在微信浏览器中打开
    const ua = navigator.userAgent.toLowerCase()
    const isWeChatBrowser = /micromessenger/.test(ua)
    setIsWeChat(isWeChatBrowser)
  }, [])

  const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info') => {
    setToast({ show: true, message, type })
  }

  const closeToast = () => {
    setToast({ show: false, message: '', type: 'info' })
  }

  const handleAndroidDownload = async () => {
    if (isWeChat) {
      showToast('请在浏览器中打开此页面进行下载', 'info')
      return
    }

    try {
      const data = await api.getAppConfig('android')

      if (data?.url) {
        // 使用返回的 URL 进行下载
        const link = document.createElement('a')
        link.href = data.url
        link.download = `hrqt-${data.version || 'latest'}.apk`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        showToast('下载开始，请查看浏览器下载列表', 'success')
      } else {
        showToast('获取下载链接失败，请稍后重试', 'error')
      }
    } catch (error) {
      console.error('下载失败:', error)
      showToast('下载失败，请稍后重试', 'error')
    }
  }

  const handleIOSDownload = async () => {
    if (isWeChat) {
      showToast('请在浏览器中打开此页面进行下载', 'info')
      return
    }

    try {
      const data = await api.getAppConfig('ios')

      if (data?.url) {
        // 使用返回的 URL 进行下载
        window.location.href = data.url
        showToast('正在跳转到下载页面...', 'success')
      } else {
        showToast('iOS 版本暂未开放，敬请期待！', 'info')
      }
    } catch (error) {
      console.error('获取下载链接失败:', error)
      showToast('iOS 版本暂未开放，敬请期待！', 'info')
    }
  }

  return (
    <div className="home-container">
      {/* Header */}
      <header className="main-header">
        <div className="header-content">
          <div className="logo-area">
            <span className="logo-symbol">🏢</span>
            <span className="logo-text">华融企泰</span>
          </div>
          <nav className="main-nav">
            <a href="#home">首页</a>
            <a href="#products">产品展示</a>
            <a href="#features">特色功能</a>
            <a href="#about">关于我们</a>
            <a href="#contact">联系我们</a>
          </nav>
          <button className="header-download-btn" onClick={() => document.getElementById('download')?.scrollIntoView({ behavior: 'smooth' })}>
            下载 APP
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="hero-section">
        <div className="hero-content">
          <h1 className="company-name">华融企泰</h1>
          <p className="company-slogan">数改创造活力，数字资产创造价值</p>
          <p className="company-description">
            助力中国数字化建设，为中小实体企业构建安全、严谨、高效的数字资产流转结算创新型服务平台
          </p>
          <div className="hero-buttons">
            <button className="hero-primary-btn" onClick={() => document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' })}>
              了解产品
            </button>
            <button className="hero-secondary-btn" onClick={() => document.getElementById('download')?.scrollIntoView({ behavior: 'smooth' })}>
              立即下载
            </button>
          </div>
        </div>
      </section>

      {/* Product Display Section */}
      <section id="products" className="products-section">
        <div className="container">
          <h2 className="section-title">产品展示</h2>
          <div className="products-grid">
            <div className="product-item">
              <div className="mockup-frame">
                <img src={homeImg} alt="资产管理首页" className="mockup-image" />
              </div>
              <h3 className="product-label">资产管理首页</h3>
            </div>
            <div className="product-item">
              <div className="mockup-frame">
                <img src={detailsImg} alt="交易结算明细" className="mockup-image" />
              </div>
              <h3 className="product-label">交易结算明细</h3>
            </div>
            <div className="product-item">
              <div className="mockup-frame">
                <img src={walletImg} alt="钱包页" className="mockup-image" />
              </div>
              <h3 className="product-label">钱包页</h3>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="features" className="features-section">
        <div className="container">
          <h2 className="section-title">核心服务</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">💼</div>
              <h3 className="feature-title">数字资产结算</h3>
              <p className="feature-desc">为企业提供数字资产流转结算服务，提升资金周转效率</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🔧</div>
              <h3 className="feature-title">技术支持</h3>
              <p className="feature-desc">专业的技术开发能力，保障系统稳定运行与多维对接</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📋</div>
              <h3 className="feature-title">规范管理</h3>
              <p className="feature-desc">完整的规范管理体系，确保各项业务流程标准透明</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🛡️</div>
              <h3 className="feature-title">安全保障</h3>
              <p className="feature-desc">严谨的风控机制与安全保护规范，确保业务合法合规</p>
            </div>
          </div>
        </div>
      </section>

      {/* Strategy Section */}
      <section className="strategy-section">
        <div className="container">
          <h2 className="section-title">四大战略</h2>
          <div className="strategy-grid">
            <div className="strategy-card">
              <div className="strategy-number">01</div>
              <h3 className="strategy-title">提升核心竞争力</h3>
              <p className="strategy-desc">打造全方位数字生态，五年内实现平台用户注册量达1亿目标</p>
            </div>
            <div className="strategy-card">
              <div className="strategy-number">02</div>
              <h3 className="strategy-title">推动数字化转型</h3>
              <p className="strategy-desc">引领中小企业转型，计划两到三年内实现挂牌企业数达500家</p>
            </div>
            <div className="strategy-card">
              <div className="strategy-number">03</div>
              <h3 className="strategy-title">形成标杆平台</h3>
              <p className="strategy-desc">搭建体系化结算服务，引导数字经济行业标准化与规范化建设</p>
            </div>
            <div className="strategy-card">
              <div className="strategy-number">04</div>
              <h3 className="strategy-title">培养核心人才</h3>
              <p className="strategy-desc">设立数字经济研究中心，挖掘领域潜能，为社会输送专业化人才</p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about-section">
        <div className="container">
          <h2 className="section-title">关于我们</h2>
          <div className="about-layout">
            <div className="about-text-content">
              <p className="about-main-text">
                华融企泰主要面向国内中小实体企业，提供数字资产流转结算与全面技术支持。我们致力于打造具有行业影响力的数字资产创新服务平台，助力中国实体经济实现高质量数字化转型。
              </p>
              <div className="stats-row">
                <div className="stat-item">
                  <div className="stat-number">1亿+</div>
                  <div className="stat-label">目标用户</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">500+</div>
                  <div className="stat-label">挂牌企业</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">24/7</div>
                  <div className="stat-label">持续服务</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section (Refactored) */}
      <section className="intro-section">
        <div className="container">
          <div className="intro-card">
            <h2 className="intro-title">助力中国数字化建设</h2>
            <p>
              在数字时代，数据要素与数字技术贯穿国内循环全过程。华融企泰诞生于数字化大潮，旨在通过技术创新，建立适用中国企业发展的新模式，为中国实体经济腾飞注入新能量。
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <div className="container">
          <h2 className="section-title">联系我们</h2>
          <div className="contact-grid">
            <div className="contact-card">
              <div className="contact-icon">📧</div>
              <h3>联系邮箱</h3>
              <p>zjhrqt@163.com</p>
            </div>
            <div className="contact-card">
              <div className="contact-icon">📍</div>
              <h3>公司地址</h3>
              <p>浙江省宁波市高新区聚贤街道扬帆路999弄2号</p>
            </div>
            <div className="contact-card">
              <div className="contact-icon">⏰</div>
              <h3>服务时间</h3>
              <p>周一至周五 9:00 - 18:00</p>
            </div>
          </div>
        </div>
      </section>

      {/* Download Section */}
      <section id="download" className="download-section">
        <div className="container">
          <h2 className="section-title">下载华融企泰 APP</h2>
          <p className="download-intro">随时随地管理您的数字资产，开启高效结算新体验</p>
          <div className="download-btns">
            <button className="download-btn android" onClick={handleAndroidDownload}>
              <span className="btn-icon">🤖</span>
              <div className="btn-text">
                <span className="platform">Android</span>
                <span className="action">立即下载</span>
              </div>
            </button>
            <button className="download-btn ios" onClick={handleIOSDownload}>
              <span className="btn-icon">🍎</span>
              <div className="btn-text">
                <span className="platform">iPhone</span>
                <span className="action">敬请期待</span>
              </div>
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <p className="footer-company">浙江华融企泰数字科技有限公司 版权所有</p>
          <div className="footer-info">
            <span className="info-item">备案号：<a href="https://beian.miit.gov.cn/" target="_blank" rel="noreferrer">浙ICP备2024126734号-3</a></span>
          </div>
          <div className="footer-links">
            <a href="/article-detail/terms_service" className="footer-link">用户协议</a>
            <span className="link-separator">|</span>
            <a href="/article-detail/privacy_policy_statement" className="footer-link">隐私政策</a>
          </div>
          <p className="footer-copyright">© 2022-{new Date().getFullYear()} 华融企泰</p>
        </div>
      </footer>

      {/* Toast Component */}
      <Toast show={toast.show} message={toast.message} type={toast.type} onClose={closeToast} />
    </div>
  )
}

export default Home
